import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ModalComponent from "../../components/modalComponent";
import TableComponent from "../../components/tableComponent";
import { actionsColumn, textColumn } from "../../components/tableComponent/columns";
import StatusPill from "../../components/statusPill";
import ButtonComponent from "../../components/button";
import icons from "../../constants/icons";
import EditUserLicenseModal from "./editUserLicenseModal";
import {
  callApiAsync,
  getUsersByBusinessUnit,
  userSysLicenseCreateOrUpdate
} from "../../services/apiService";
import ToastNotification from "../../components/toastNotification";
import { user_license_status_select } from "../../utils/selectsValues";

const licenseLabel = (status) =>
  user_license_status_select.find((o) => o.value === status)?.label ?? "Activa";

const licensePillColor = (status) => {
  if (status === 1) return "#4ADE80";
  if (status === 2) return "#FCA5A5";
  return "#D9D9D9";
};

const UserLicensesModal = ({ open, onClose, businessUnitRow }) => {
  const userData = useSelector((state) => state.user?.userData);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [openEditLicense, setOpenEditLicense] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const loadUsers = () => {
    if (!businessUnitRow?.id) return;
    callApiAsync(
      () => getUsersByBusinessUnit(businessUnitRow.id),
      (data) => setUsers(Array.isArray(data) ? data : [])
    );
  };

  useEffect(() => {
    if (open) loadUsers();
  }, [open, businessUnitRow?.id]);

  const saveLicense = (userRow, newStatus) => {
    callApiAsync(
      () =>
        userSysLicenseCreateOrUpdate({
          businessUnitId: businessUnitRow.id,
          userId: userRow.id,
          licenseStatus: Number(newStatus),
          expiresAt: userRow.expiresAt ?? null,
          email: userData?.email ?? ""
        }),
      () => {
        setToastSeverity("success");
        setToastMessage("Licencia de usuario actualizada.");
        setToastOpen(true);
        setOpenEditLicense(false);
        setSelectedUser(null);
        loadUsers();
      },
      (err) => {
        setToastSeverity("error");
        setToastMessage(err?.message || err || "Error al guardar.");
        setToastOpen(true);
      }
    );
  };

  const openEdit = (row) => {
    setSelectedUser(row);
    setOpenEditLicense(true);
  };

  const userActionBtn = ({ row }) => (
    <ButtonComponent
      onClick={() => openEdit(row.original)}
      variant="contained"
      backgroundColor="#fff0"
      title="Editar licencia"
    >
      <img className="btn-icon" src={icons.blue_edit} alt="Editar licencia" />
    </ButtonComponent>
  );

  const columns = useMemo(
    () => [
      textColumn("lastName", "Apellido", 120),
      textColumn("name", "Nombre", 120),
      textColumn("email", "Email", 200),
      textColumn("position", "Cargo", 140),
      {
        id: "licenseStatus",
        header: "Licencia",
        size: 130,
        Cell: ({ row }) => {
          const status = row.original.licenseStatus ?? 1;
          return (
            <StatusPill backgroundColor={licensePillColor(status)} textColor="#000">
              {licenseLabel(status)}
            </StatusPill>
          );
        }
      },
      actionsColumn(userActionBtn, 80, "Acciones")
    ],
    []
  );

  const title = businessUnitRow?.name
    ? `Usuarios — ${businessUnitRow.name}`
    : "Usuarios del laboratorio";

  return (
    <>
      <ModalComponent open={open} onClose={onClose} title={title} customWidth="900px">
        <p style={{ marginBottom: 12, color: "#555", fontSize: 14 }}>
          Licencia por usuario en este lab. Use Acciones para cambiar el estado.
        </p>
        <TableComponent columns={columns} data={users} enableGlobalFilter={true} />
      </ModalComponent>
      <EditUserLicenseModal
        open={openEditLicense}
        onClose={() => {
          setOpenEditLicense(false);
          setSelectedUser(null);
        }}
        userRow={selectedUser}
        onSubmit={saveLicense}
      />
      <ToastNotification
        open={toastOpen}
        handleClose={() => setToastOpen(false)}
        message={toastMessage}
        severity={toastSeverity}
      />
    </>
  );
};

export default UserLicensesModal;
